/**
 * App-wide connectivity signal (ISS-445).
 *
 * One shared ReachabilityMonitor instance for the whole app: the Firestore
 * read helpers (lib/firestore/earnedRead.ts) report real read outcomes into
 * it, and UI (components/shared/DegradedReadState.tsx) subscribes so a failed
 * surface can retry itself the moment signal returns.
 *
 * The active probe asks the actual backend host — reaching Firebase Hosting
 * (same-origin assets) proves nothing about Firestore on a weak connection.
 * `mode: 'no-cors'` keeps it a simple request (no preflight); ANY response,
 * including an opaque 404, proves the host is reachable. Only a network
 * error / timeout counts as unreachable.
 */
import { ReachabilityMonitor } from '@officehub/offline-sdk';

const FIRESTORE_HOST_PROBE_URL = 'https://firestore.googleapis.com/';
const PROBE_TIMEOUT_MS = 5_000;
const PROBE_INTERVAL_MS = 10_000;

async function probeFirestoreHost(): Promise<boolean> {
  try {
    await fetch(FIRESTORE_HOST_PROBE_URL, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return true;
  } catch {
    return false;
  }
}

export const reachability = new ReachabilityMonitor({
  probe: probeFirestoreHost,
  probeIntervalMs: PROBE_INTERVAL_MS,
});
